import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import type Item from '../../@types/item';
import type { RelatedItemCollection } from '../../@types/item';
import { getMissingItem } from '../../actions/missing-item';
import { createPageTitle } from '../../helpers/text';
import type { RootState } from '../../reducers';
import { Container } from '../../styled-components/components/Container';
import { H1 } from '../../styled-components/components/Heading';
import { RouterLink } from '../../styled-components/components/Link';
import Loader from '../../styled-components/components/Loader';
import { MaxWidth } from '../../styled-components/components/MaxWidth';
import { Page, PageSection } from '../../styled-components/components/Page';
import { Text } from '../../styled-components/components/Text';
import { VStack } from '../../styled-components/components/VStack';
import { TEXT_CONTENT_MAX_WIDTH } from '../../styled-components/layout/constants';
import MostSimilarItems from './most-similar';
import ItemCollection from './related-collection';

interface IRelatedItemsProps {
  mostSimilarItems: Item[];
  similarLink: string;
  itemCollections: RelatedItemCollection[];
}

const RelatedItems = ({ mostSimilarItems, similarLink, itemCollections }: IRelatedItemsProps) => (
  <>
    {!!mostSimilarItems.length && (
      <MostSimilarItems items={mostSimilarItems} similarLink={similarLink} />
    )}
    {itemCollections.map((collection, index) => (
      <ItemCollection collection={collection} key={`${index}-${collection?.title}`} />
    ))}
  </>
);

interface IProps {
  id: string;
}

const MissingItem = ({ id }: IProps) => {
  const { t } = useTranslation('error');
  const dispatch = useDispatch();
  const missingItemId = useSelector<RootState, string>((s) => s.missingItem.id);
  const isFetching = useSelector<RootState, boolean>((s) => s.missingItem.isFetching);
  const mostSimilarItems = useSelector<RootState, Item[]>((s) => s.missingItem.mostSimilarItems);
  const similarLink = useSelector<RootState, string>((s) => s.missingItem.similarLink);
  const itemCollections = useSelector<RootState, RelatedItemCollection[]>(
    (s) => s.missingItem.relatedItemsCollections,
  );
  const actions = bindActionCreators({ getMissingItem }, dispatch);

  useEffect(() => {
    document.title = createPageTitle(t('pageNotFound', { ns: 'common' }));
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }
    const shouldFetch = !isFetching && missingItemId !== id;
    if (shouldFetch) {
      actions.getMissingItem(id);
    }
  }, [id, missingItemId, isFetching]);

  const hasRelatedItems = !isFetching && missingItemId === id;

  return (
    <Page>
      <PageSection>
        <Container>
          <MaxWidth $maxWidth={TEXT_CONTENT_MAX_WIDTH}>
            <VStack $spacing={2}>
              <H1 $margin="none">{t('weCanNotFindTheVehicle', { ns: 'error' })}</H1>
              <Text $size="large" $weight="bold">
                {t('presumablyTheCarHasBeenSoldOrRemoved', { ns: 'error' })}
              </Text>
              <Text>
                <Trans
                  i18nKey="error:seeSimilarCars"
                  values={{
                    linkText: t('seeSimilarCarsLinkText', { ns: 'error' }),
                  }}
                  components={[
                    <RouterLink
                      key="email-link"
                      to="/sok"
                      title={
                        t('seeSimilarCarsLinktitle', {
                          ns: 'error',
                        }) || ''
                      }
                    />,
                  ]}
                />
              </Text>
            </VStack>
          </MaxWidth>
        </Container>
      </PageSection>
      {isFetching && (
        <PageSection>
          <Container>
            <Loader />
          </Container>
        </PageSection>
      )}
      {hasRelatedItems && (
        <RelatedItems
          mostSimilarItems={mostSimilarItems}
          similarLink={similarLink}
          itemCollections={itemCollections}
        />
      )}
    </Page>
  );
};

export default MissingItem;
